import { Slot, Slottable } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";
import { Button } from "../Button";
import type { BadgeProps } from "./Badge";
import { badgeVariants } from "./styles";

// The action button's box and glyph per badge size. `Button`'s own `xs` size is the closest fit but
// is still built for a standalone control, so both are overridden here to sit inside the pill.
const ACTION_SIZE = {
  xs: "size-3 [&_svg:not([class*='size-'])]:size-2",
  sm: "size-3.5 [&_svg:not([class*='size-'])]:size-2.5",
  md: "size-4 [&_svg:not([class*='size-'])]:size-3",
  lg: "size-5 [&_svg:not([class*='size-'])]:size-3.5",
} as const;

export type BadgeBodyProps = Omit<BadgeProps, "hoverLabel">;

export const BadgeBody = ({
  className,
  variant = "solid",
  color = "primary",
  size = "md",
  actionProps,
  asChild,
  children,
  ref,
  ...props
}: BadgeBodyProps) => {
  const ActionIcon = actionProps?.icon;
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      ref={ref}
      className={cn(badgeVariants({ variant, color, size, hasAction: !!actionProps }), className)}
      {...props}
      // After the spread: a wrapping `Tooltip.Trigger` passes its own `data-slot` down, and the badge
      // must still identify as one.
      data-slot="badge"
    >
      <Slottable>{children}</Slottable>
      {!!ActionIcon && (
        <Button
          variant="ghost"
          color="muted"
          size="xs"
          iconOnly
          hoverLabel={actionProps.hoverLabel}
          onClick={actionProps.onClick}
          // `text-current` inherits whatever foreground the variant/color pair set, so the icon
          // stays legible on a solid fill without this needing to know each color's on-tone.
          className={cn(
            "rounded-sm bg-transparent text-current hover:bg-black/10 dark:hover:bg-white/20",
            ACTION_SIZE[size]
          )}
        >
          <ActionIcon />
        </Button>
      )}
    </Comp>
  );
};
